import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { addDoc, collection, deleteDoc, doc, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import './AdminGradingView.css';
import { scopeForAdmin, schoolMatches } from '../utils/schoolScope';

function SuperAdminGradingView({ adminData }) {
  const [saSchoolId, setSaSchoolId] = useState('');
  const [saSchools, setSaSchools] = useState([]);
  useEffect(() => {
    getDocs(collection(db, 'schools')).then(snap => setSaSchools(snap.docs.map(d => ({ id: d.id, ...d.data() })))).catch(() => {});
  }, []);
  // Dropdown is authoritative: "All Schools" ('' ) = national view
  adminData = useMemo(() => ({ ...(adminData || {}), schoolId: saSchoolId || null }), [adminData, saSchoolId]);
  const [gradingScales, setGradingScales] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(true);

  const [newScale, setNewScale] = useState({
    name: '',
    minScore: 90,
    maxScore: 100,
    grade: 'A',
    gpa: 4.0
  });

  const loadGradingScales = useCallback(async () => {
    console.log('🔥 AdminGrading: Loading grading scales');
    try {
      const snapshot = await getDocs(collection(db, 'gradingScales'));
      const scalesData = scopeForAdmin(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })), adminData);
      console.log('🔥 AdminGrading: Grading scales loaded', scalesData.length, scalesData);
      setGradingScales(scalesData);
    } catch (error) {
      console.error('🔥 AdminGrading: Error loading grading scales:', error);
    } finally {
      setLoading(false);
    }
  }, [adminData]);

  useEffect(() => {
    loadGradingScales();
  }, [loadGradingScales]);

  const handleAddScale = async (e) => {
    e.preventDefault();
    try {
      await addDoc(collection(db, 'gradingScales'), {
        ...newScale,
        createdAt: new Date().toISOString(),
        schoolId: adminData?.schoolId || null
      });
      setShowAddForm(false);
      setNewScale({
        name: '',
        minScore: 90,
        maxScore: 100,
        grade: 'A',
        gpa: 4.0
      });
      loadGradingScales();
    } catch (error) {
      console.error('Error adding grading scale:', error);
      alert('Failed to add grading scale');
    }
  };

  const handleDeleteScale = async (scaleId) => {
    if (!confirm('Are you sure you want to delete this grading scale?')) return;
    try {
      await deleteDoc(doc(db, 'gradingScales', scaleId));
      loadGradingScales();
    } catch (error) {
      console.error('Error deleting grading scale:', error);
      alert('Failed to delete grading scale');
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="admin-grading-view">
      <div className="view-header">
        <h2>Grading Scale</h2>
        <div className="sa-school-filter"><label>School:</label><select value={saSchoolId} onChange={(e) => setSaSchoolId(e.target.value)}><option value="">All Schools</option>{saSchools.map(s => <option key={s.id} value={s.id}>{s.name || s.id}</option>)}</select></div>
        <button className="add-button" onClick={() => setShowAddForm(true)}>
          + Add Scale
        </button>
      </div>

      <p className="count-text">Grading Scales: {gradingScales.length}</p>

      {showAddForm && (
        <div className="add-scale-form">
          <h3>Create New Grading Scale</h3>
          <form onSubmit={handleAddScale}>
            <div className="form-row">
              <div className="form-group">
                <label>Grade *</label>
                <input
                  type="text"
                  value={newScale.grade}
                  onChange={(e) => setNewScale({ ...newScale, grade: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={newScale.name}
                  onChange={(e) => setNewScale({ ...newScale, name: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Min Score *</label>
                <input
                  type="number"
                  value={newScale.minScore}
                  onChange={(e) => setNewScale({ ...newScale, minScore: parseFloat(e.target.value) })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Max Score *</label>
                <input
                  type="number"
                  value={newScale.maxScore}
                  onChange={(e) => setNewScale({ ...newScale, maxScore: parseFloat(e.target.value) })}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label>GPA *</label>
              <input
                type="number"
                step="0.1"
                value={newScale.gpa}
                onChange={(e) => setNewScale({ ...newScale, gpa: parseFloat(e.target.value) })}
                required
              />
            </div>
            <div className="form-actions">
              <button type="button" onClick={() => setShowAddForm(false)}>
                Cancel
              </button>
              <button type="submit">Create Scale</button>
            </div>
          </form>
        </div>
      )}

      {gradingScales.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🎓</div>
          <h3>No Grading Scales</h3>
          <p>Create your first grading scale to get started</p>
        </div>
      ) : (
        <div className="scales-list">
          {gradingScales.map(scale => (
            <div key={scale.id} className="scale-card">
              <div className="scale-grade">
                <span className="grade-letter">{scale.grade}</span>
              </div>
              <div className="scale-info">
                <h4>{scale.name}</h4>
                <p>Range: {scale.minScore}% - {scale.maxScore}%</p>
                <p>GPA: {scale.gpa}</p>
                <p>🏫 {saSchools.find(s => schoolMatches(s.id, scale.schoolId))?.name || scale.schoolId || 'All Schools'}</p>
              </div>
              <button
                className="delete-button"
                onClick={() => handleDeleteScale(scale.id)}
              >
                🗑️
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default SuperAdminGradingView;
